/**
 * LearnerNav — shared top navigation bar for all learner pages.
 *
 * Layout (left → right):
 *  - TeachIQ logo (64×64) — click navigates to /dashboard
 *  - "Dashboard" text link — explicit breadcrumb back to home
 *  - SearchBar — grows to fill middle
 *  - NotificationBell
 *  - Learner display name (truncated)
 *  - Icon-only nav buttons (label shown in tooltip on hover)
 *  - Log Out icon button
 *
 * Icon-only buttons prevent overflow on smaller screens while the
 * native title tooltip always shows the full label on hover.
 */

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useBranding } from '../../context/BrandingContext';
import SearchBar from './SearchBar';
import NotificationBell from './NotificationBell';

// Marketplace nav temporarily hidden — pricing labels / specialty scoping are
// not ready for learner-facing demos. Route remains for deep links / QA.
const SHOW_MARKETPLACE_NAV = false;

const NAV_LINKS = [
  { path: '/dashboard',            icon: '🏠', label: 'Dashboard' },
  { path: '/profile',              icon: '👤', label: 'Profile' },
  { path: '/dashboard/progress',   icon: '📊', label: 'My Progress' },
  { path: '/dashboard/resources',  icon: '📚', label: 'My Library' },
  { path: '/my-notes',             icon: '📝', label: 'My Notes' },
  { path: '/my/submissions',       icon: '📋', label: 'My Submissions' },
  ...(SHOW_MARKETPLACE_NAV
    ? [{ path: '/marketplace', icon: '🏪', label: 'Marketplace' }]
    : []),
];

export default function LearnerNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { learner, logout } = useAuth();
  const { branding } = useBranding();
  const logoSrc = branding?.logo_url || '/teachiq-logo.png';
  const logoAlt = branding?.display_name || 'TeachIQ';

  return (
    <header style={headerStyle}>
      {/* Logo — 64×64, flush top-left (tenant branding.logo_url when set) */}
      <button
        onClick={() => navigate('/dashboard')}
        aria-label="Go to dashboard"
        style={logoBtnStyle}
      >
        <img src={logoSrc} alt={logoAlt} style={logoImgStyle} />
      </button>

      {/* Search bar — grows to fill middle */}
      <div style={{ flex: 1, padding: '0 12px', minWidth: 0 }}>
        <SearchBar onNavigate={(path) => navigate(path)} />
      </div>

      {/* Right side: icon-only nav + bell + user + logout — all stay inside the bar */}
      <div style={rightSideStyle}>
        <NotificationBell onNavigate={(path) => navigate(path)} />

        {learner?.display_name && (
          <span style={welcomeStyle} title={learner.display_name}>
            {learner.display_name}
          </span>
        )}

        {/* Divider */}
        <span style={dividerStyle} />

        {NAV_LINKS.map(({ path, icon, label }) => {
          const active = location.pathname === path ||
            (path !== '/dashboard' && location.pathname.startsWith(path));
          return (
            <button
              key={path}
              onClick={() => navigate(path)}
              title={label}
              aria-label={label}
              style={{
                ...iconBtnBase,
                background: active ? 'rgba(146,20,12,0.35)' : 'transparent',
                color: active ? '#FFCF99' : 'rgba(255,248,240,0.85)',
                borderBottom: active ? '3px solid #FFCF99' : '3px solid transparent',
              }}
            >
              <span style={{ fontSize: 18, lineHeight: 1 }}>{icon}</span>
            </button>
          );
        })}

        <span style={dividerStyle} />

        <button
          onClick={logout}
          title="Log Out"
          aria-label="Log Out"
          style={{ ...iconBtnBase, color: 'rgba(255,248,240,0.7)', borderBottom: '3px solid transparent' }}
        >
          <span style={{ fontSize: 18, lineHeight: 1 }}>🚪</span>
        </button>
      </div>
    </header>
  );
}

// ── Styles ──────────────────────────────────────────────────────────────────

const headerStyle = {
  position: 'sticky',
  top: 0,
  zIndex: 300,
  height: 64,
  background: '#1E1E24',
  borderBottom: '3px solid #FFCF99',
  boxShadow: '0 2px 12px rgba(0,0,0,0.30)',
  display: 'flex',
  alignItems: 'center',
  padding: 0,
  width: '100%',
  boxSizing: 'border-box',
  // visible so NotificationBell / SearchBar dropdowns are not clipped
  overflow: 'visible',
};

const logoBtnStyle = {
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  height: 64,
  width: 64,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
};

const logoImgStyle = {
  height: 64,
  width: 64,
  objectFit: 'cover',
  display: 'block',
};

const rightSideStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 0,
  paddingRight: 6,
  flexShrink: 0,        /* never shrinks — fixed width icon strip */
};

const welcomeStyle = {
  color: 'rgba(255,248,240,0.55)',
  fontSize: 11,
  padding: '0 8px',
  whiteSpace: 'nowrap',
  maxWidth: 100,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  flexShrink: 1,
};

const dividerStyle = {
  display: 'block',
  width: 1,
  height: 28,
  background: 'rgba(255,207,153,0.25)',
  margin: '0 4px',
  flexShrink: 0,
};

/* Icon-only button — 44px wide, full header height */
const iconBtnBase = {
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 44,
  height: 64,
  flexShrink: 0,
  borderRadius: 0,
  transition: 'background 0.15s, color 0.15s',
  fontFamily: 'inherit',
  padding: 0,
};
